import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { formatPhoneFR } from "@/lib/phone";
import { X, FileText, CreditCard, MessageCircle, MapPin, Stethoscope, Home, Phone as PhoneIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { toneForType } from "@/lib/careTones";

const pad = (n) => String(n).padStart(2, "0");
const hhmm = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const DUREES = [15, 30, 45, 60, 90, 120];
const STATUTS = [
  { v: "Planifié", label: "Planifié", dot: "oklch(0.55 0.13 255)" },
  { v: "Soin délivré", label: "Soin délivré", dot: "oklch(0.55 0.12 155)" },
  { v: "À clôturer", label: "À clôturer", dot: "oklch(0.7 0.15 75)" },
  { v: "Clôturé", label: "Clôturé", dot: "oklch(0.5 0.12 155)" },
  { v: "Annulé", label: "Annulé", dot: "var(--muted-2)" },
];
const iconForType = (nom) => { const n = (nom || "").toLowerCase(); if (n.includes("domicile")) return Home; if (n.includes("tél") || n.includes("tel")) return PhoneIcon; return Stethoscope; };
const eur = (n) => (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

export function RdvApercu({ rdvId, orderedIds, onClose, onNavigate, onSaved, careTypes, secteurs, patients, pas = 30 }) {
  const nav = useNavigate();
  const [rdv, setRdv] = useState(null);
  const [editTime, setEditTime] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [saveState, setSaveState] = useState("idle");
  const panelRef = useRef(null);

  // Volet flottant : se ferme au clic en dehors (y compris hors de l'agenda),
  // sauf si le clic vise un dialog / la palette / le volet de création, ou une
  // colonne de l'agenda (c'est alors la grille qui gère la fermeture via onSlot,
  // sans ouvrir « Nouveau RDV »).
  useEffect(() => {
    const onDown = (e) => {
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest("[role='dialog'], [data-testid='creer-rdv-volet'], [data-testid='command-palette'], [data-testid^='week-col-']")) return;
      onClose();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [onClose]);

  useEffect(() => {
    setEditTime(false); setConfirmCancel(false);
    api.get(`/rdvs/${rdvId}`).then((r) => setRdv(r.data)).catch(() => setRdv(null));
  }, [rdvId]);

  useEffect(() => {
    const onKey = (e) => {
      if (document.querySelector("[data-testid='creer-rdv-volet'], [data-testid='command-palette'], [role='dialog']")) return;
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
      else if (e.key === "ArrowLeft" && !editTime) { const i = orderedIds.indexOf(rdvId); if (i > 0) onNavigate(orderedIds[i - 1]); }
      else if (e.key === "ArrowRight" && !editTime) { const i = orderedIds.indexOf(rdvId); if (i >= 0 && i < orderedIds.length - 1) onNavigate(orderedIds[i + 1]); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [rdvId, orderedIds, editTime, onClose, onNavigate]);

  const patient = useMemo(() => patients.find((p) => p.id === rdv?.patient_id), [patients, rdv]);
  const careType = useMemo(() => careTypes.find((c) => c.id === rdv?.type_soin), [careTypes, rdv]);
  const secteur = useMemo(() => secteurs.find((s) => s.id === rdv?.secteur_id), [secteurs, rdv]);

  const patch = async (body) => {
    setSaveState("saving");
    try { const { data } = await api.patch(`/rdvs/${rdvId}`, body); setRdv(data); setSaveState("saved"); onSaved && onSaved(); }
    catch (e) { setSaveState("error"); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!rdv) return (
    <div ref={panelRef} className="fixed z-30 bg-white flex items-center justify-center sx-fade-in" style={{ top: 88, right: 16, bottom: 16, width: 300, borderRadius: 14, boxShadow: "var(--sh-float)" }}>
      <span className="text-sm" style={{ color: "var(--muted-2)" }}>Chargement…</span>
    </div>
  );

  const st = new Date(rdv.date);
  const en = rdv.end ? new Date(rdv.end) : new Date(st.getTime() + (rdv.duree_min || 60) * 60000);
  const paid = (rdv.montant_paye || 0) >= (rdv.montant_du || 0) && (rdv.montant_du || 0) > 0;
  const startOpts = (() => { const o = []; for (let m = 7 * 60; m <= 21 * 60; m += pas) o.push(`${pad(Math.floor(m / 60))}:${pad(m % 60)}`); return o; })();
  const idx = orderedIds.indexOf(rdvId);
  const setStart = (val) => { const d = new Date(rdv.date); const [h, mn] = val.split(":").map(Number); d.setHours(h, mn, 0, 0); patch({ date: d.toISOString() }); };
  const setDay = (val) => { const d = new Date(rdv.date); const nd = new Date(val); d.setFullYear(nd.getFullYear(), nd.getMonth(), nd.getDate()); patch({ date: d.toISOString() }); };

  const Detail = ({ k, children }) => (
    <div className="flex items-center justify-between py-1.5 text-[13px]"><span style={{ color: "var(--muted)" }}>{k}</span><span className="text-right" style={{ color: "var(--ink)" }}>{children}</span></div>
  );

  return (
    <div ref={panelRef} className="fixed z-30 bg-white flex flex-col sx-fade-in" style={{ top: 88, right: 16, bottom: 16, width: 300, borderRadius: 14, boxShadow: "var(--sh-float)", overflow: "hidden" }} data-testid="rdv-apercu">
      <div className="flex-1 overflow-y-auto" style={{ padding: 20 }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-[12px]" style={{ color: "var(--muted-2)" }}>Rendez-vous</span>
            <div className="flex items-center gap-0.5">
              <button data-testid="rdv-prev" disabled={idx <= 0} onClick={() => onNavigate(orderedIds[idx - 1])} className="sx-icon-btn disabled:opacity-30" aria-label="RDV précédent"><ChevronLeft className="w-3.5 h-3.5" /></button>
              <button data-testid="rdv-next" disabled={idx < 0 || idx >= orderedIds.length - 1} onClick={() => onNavigate(orderedIds[idx + 1])} className="sx-icon-btn disabled:opacity-30" aria-label="RDV suivant"><ChevronRight className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          <button data-testid="rdv-apercu-close" onClick={onClose} className="sx-icon-btn" aria-label="Fermer"><X className="w-4 h-4" /></button>
        </div>

        {/* Date/heure éditable en place */}
        {!editTime ? (
          <button data-testid="rdv-time-display" onClick={() => setEditTime(true)} className="mt-3 text-[13px] text-left rounded px-1.5 py-1 -mx-1.5 hover:ring-1" style={{ color: "var(--muted)", boxShadow: "inset 0 0 0 0 transparent" }}>
            <span className="capitalize">{st.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })}</span> · {hhmm(st)} – {hhmm(en)}
          </button>
        ) : (
          <div className="mt-3 space-y-1.5" data-testid="rdv-time-edit">
            <input type="date" data-testid="rdv-edit-date" value={`${st.getFullYear()}-${pad(st.getMonth() + 1)}-${pad(st.getDate())}`} onChange={(e) => e.target.value && setDay(e.target.value)} className="w-full sx-input" style={{ fontSize: 13 }} />
            <div className="flex gap-1.5">
              <select data-testid="rdv-edit-start" value={hhmm(st)} onChange={(e) => setStart(e.target.value)} className="flex-1 sx-input" style={{ fontSize: 13 }}>
                {startOpts.includes(hhmm(st)) ? null : <option value={hhmm(st)}>{hhmm(st)}</option>}
                {startOpts.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <select data-testid="rdv-edit-duree" value={rdv.duree_min || 60} onChange={(e) => patch({ duree_min: Number(e.target.value) })} className="flex-1 sx-input" style={{ fontSize: 13 }}>
                {DUREES.includes(rdv.duree_min) ? null : <option value={rdv.duree_min}>{rdv.duree_min} min</option>}
                {DUREES.map((d) => <option key={d} value={d}>{d} min</option>)}
              </select>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[11.5px]" style={{ color: "var(--muted-2)" }}>Fin : {hhmm(en)}</span>
              <button onClick={() => setEditTime(false)} className="text-[12px]" style={{ color: "var(--muted)" }}>OK</button>
            </div>
          </div>
        )}

        {/* Nom patient */}
        <button data-testid="rdv-patient-link" onClick={() => rdv.patient_id && nav(`/patients/${rdv.patient_id}`)} className="block mt-2 font-bold text-left tracking-[-0.01em] hover:underline" style={{ fontSize: 22, color: "var(--ink)" }}>
          {rdv.patient_nom || rdv.titre || "RDV"}
        </button>

        {/* Pastilles */}
        <div className="flex flex-wrap gap-1.5 mt-3">
          <span className="sx-badge" style={{ background: paid ? "oklch(0.95 0.04 155)" : (rdv.montant_du ? "oklch(0.95 0.04 25)" : "var(--bg-warm)"), color: paid ? "oklch(0.45 0.12 155)" : (rdv.montant_du ? "oklch(0.5 0.16 25)" : "var(--muted)") }}>{paid ? "Payé" : (rdv.montant_du ? "Impayé" : "À venir")}</span>
          <label className="inline-flex items-center gap-1 sx-badge cursor-pointer" style={(() => { const t = careType ? toneForType(careType) : null; return t ? { background: t.bg, border: `1px solid ${t.border}`, color: t.ink } : { background: "var(--bg-warm)", color: "var(--text-2)" }; })()}>
            {(() => { const I = iconForType(careType?.nom); return <I className="w-3 h-3" />; })()}
            <select data-testid="rdv-type-select" value={rdv.type_soin || ""} onChange={(e) => patch({ type_soin: e.target.value })} className="bg-transparent outline-none text-[11px]">
              {!careType && <option value={rdv.type_soin || ""}>{rdv.type_soin || "Type"}</option>}
              {careTypes.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
          </label>
          <label className="inline-flex items-center gap-1 sx-badge cursor-pointer" style={{ background: "var(--bg-warm)", color: "var(--text-2)" }}>
            <MapPin className="w-3 h-3" />
            <select data-testid="rdv-secteur-select" value={rdv.secteur_id || ""} onChange={(e) => patch({ secteur_id: e.target.value || null })} className="bg-transparent outline-none text-[11px]">
              <option value="">Secteur</option>
              {secteurs.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
          </label>
          <label className="inline-flex items-center gap-1 sx-badge cursor-pointer" style={{ background: "var(--bg-warm)" }}>
            <span style={{ width: 6, height: 6, borderRadius: 99, background: (STATUTS.find((x) => x.v === rdv.statut) || STATUTS[0]).dot, display: "inline-block" }} />
            <select data-testid="rdv-statut-select" value={rdv.statut || "Planifié"} onChange={(e) => patch({ statut: e.target.value })} className="bg-transparent outline-none text-[11px]" style={{ color: "var(--text-2)" }}>
              {STATUTS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
            </select>
          </label>
        </div>

        {/* Détails */}
        <div className="mt-4 py-1" style={{ borderTop: "1px solid var(--divider)", borderBottom: "1px solid var(--divider)" }}>
          {rdv.soin_id && <Detail k="Fiche de soin"><button onClick={() => nav(`/soins/${rdv.soin_id}`)} className="mono text-[12px] hover:underline" style={{ color: "var(--id)" }}>{rdv.soin_id}</button></Detail>}
          {patient?.telephone && <Detail k="Téléphone"><a href={`tel:${patient.telephone}`} className="hover:underline">{formatPhoneFR(patient.telephone)}</a></Detail>}
          {patient?.adresse && <Detail k="Adresse">{patient.adresse}</Detail>}
          <Detail k="Montant"><span className="mono">{eur(rdv.montant_du)}{(rdv.montant_paye || 0) > 0 ? ` · réglé ${eur(rdv.montant_paye)}` : ""}</span></Detail>
        </div>

        {/* Actions */}
        {rdv.soin_id && (
          <button data-testid="rdv-open-soin" onClick={() => nav(`/soins/${rdv.soin_id}`)} className="sx-btn-dark w-full justify-center mt-4"><FileText className="w-4 h-4" style={{ color: "var(--accent)" }} />Ouvrir la fiche de soin</button>
        )}
        <div className="grid grid-cols-2 gap-2 mt-2">
          <button data-testid="rdv-encaisser" onClick={() => rdv.soin_id && nav(`/soins/${rdv.soin_id}?tab=paiements`)} className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[12.5px] font-medium" style={{ background: "var(--bg-warm)", color: "var(--ink)" }}><CreditCard className="w-4 h-4" />Encaisser</button>
          <button data-testid="rdv-sms" onClick={() => rdv.patient_id && nav(`/relation?patient=${rdv.patient_id}&modele=rappel`)} className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[12.5px] font-medium" style={{ background: "var(--bg-warm)", color: "var(--ink)" }}><MessageCircle className="w-4 h-4" />SMS</button>
        </div>

        {/* Annuler */}
        {!confirmCancel ? (
          <div className="text-center mt-4">
            <button data-testid="rdv-cancel" onClick={() => setConfirmCancel(true)} className="text-[12.5px]" style={{ color: "oklch(0.55 0.18 25)" }}>Annuler le RDV</button>
          </div>
        ) : (
          <div className="mt-4 p-3 rounded-lg" style={{ background: "oklch(0.97 0.02 25)" }} data-testid="rdv-cancel-confirm">
            <p className="text-[12.5px]" style={{ color: "var(--ink)" }}>Annuler ce RDV ? Le patient sera prévenu par SMS.</p>
            <label className="flex items-center gap-1.5 text-[12px] mt-1.5" style={{ color: "var(--text-2)" }}><input type="checkbox" defaultChecked />Prévenir par SMS <span className="text-[10.5px]" style={{ color: "var(--muted-2)" }}>(à finaliser)</span></label>
            <div className="flex gap-2 mt-2">
              <button data-testid="rdv-cancel-confirm-btn" onClick={async () => { await patch({ statut: "Annulé" }); toast.success("RDV annulé"); window.dispatchEvent(new CustomEvent("rdv-changed")); onClose(); }} className="flex-1 px-3 py-1.5 rounded-lg text-[12.5px] font-medium text-white" style={{ background: "oklch(0.55 0.18 25)" }}>Confirmer</button>
              <button onClick={() => setConfirmCancel(false)} className="flex-1 px-3 py-1.5 rounded-lg text-[12.5px] border border-slate-200">Retour</button>
            </div>
          </div>
        )}

        <div className="mt-4 text-[11px]" style={{ color: "var(--id)" }}>
          {rdv.created_at ? `Créé le ${new Date(rdv.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}` : ""}
          {saveState === "saving" ? " · Enregistrement…" : saveState === "saved" ? " · Enregistré automatiquement" : saveState === "error" ? " · Échec · Réessayer" : ""}
        </div>
      </div>
    </div>
  );
}
